import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import imageCompression
    from "browser-image-compression";

import {
    FaCheckCircle,
    FaFileInvoice,
    FaImage,
    FaPenNib,
    FaReceipt,
    FaRegEye,
    FaSignature,
    FaTrash,
    FaUpload,
} from "react-icons/fa";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchTenantProfile,
    selectIsTenantOwner,
    selectTenant,
    selectTenantInvoiceBranding,
    selectTenantInvoiceBrandingUpdateError,
    selectTenantInvoiceBrandingUpdating,
    selectTenantInvoiceBrandingUpdateSuccess,
    selectTenantInvoiceTemplate,
    selectTenantInvoiceTemplateUpdating,
    selectTenantInvoiceTemplateUpdateError,
    selectTenantInvoiceTemplateUpdateSuccess,
    updateTenantInvoiceBranding,
    updateTenantInvoiceTemplate,
} from "../../../../../store/tenantProfile/tenantProfileSlice";

import SignaturePad
    from "./SignaturePad";


const TEMPLATE_OPTIONS = [
    ["MODERN_GST", "Modern GST", "Balanced GST invoice with business, customer, tax and summary sections.", "modern"],
    ["COMPACT", "Compact", "Fits more invoice information into a clean A4 layout with reduced whitespace.", "compact"],
    ["RETAIL", "Retail", "Strong product totals and payment visibility for shops and retail billing.", "retail"],
    ["SERVICE", "Service", "Designed for repair, service, maintenance and work-based invoices.", "service"],
    ["THERMAL", "Thermal 80mm", "Receipt-style invoice for 80mm thermal printers and quick counter billing.", "thermal"],
    ["PROFESSIONAL", "Professional", "Formal corporate GST layout for B2B and professional business invoicing.", "professional"],
    ["MINIMAL", "Minimal", "Simple monochrome layout focused on clarity and low-ink printing.", "minimal"],
].map(
    (
        [
            code,
            title,
            description,
            type,
        ]
    ) => ({
        code,
        title,
        description,
        type,
    })
);


const SIGNATURE_MODES = [
    ["NONE", "No Signature", FaTrash],
    ["UPLOAD", "Upload", FaUpload],
    ["DRAW", "Draw", FaPenNib],
    ["TEXT", "Type", FaSignature],
];


const MB =
    1024 *
    1024;


const LOGO_MAX_SIZE_BYTES =
    1 *
    MB;


const LOGO_INPUT_MAX_SIZE_BYTES =
    10 *
    MB;


const SIGNATURE_MAX_SIZE_BYTES =
    350 *
    1024;


/*
|--------------------------------------------------------------------------
| FILE -> DATA URL
|--------------------------------------------------------------------------
*/

const fileToDataUrl =
    (
        file
    ) =>
        new Promise(
            (
                resolve,
                reject
            ) => {

                const reader =
                    new FileReader();


                reader.onload =
                    () =>
                        resolve(
                            String(
                                reader.result ||
                                ""
                            )
                        );


                reader.onerror =
                    () =>
                        reject(
                            new Error(
                                "Unable to read the selected image."
                            )
                        );


                reader.readAsDataURL(
                    file
                );

            }
        );


/*
|--------------------------------------------------------------------------
| VALIDATE IMAGE
|--------------------------------------------------------------------------
*/

const validateImageFile =
    (
        file,
        maxInputBytes
    ) => {

        if (
            !file
        ) {

            throw new Error(
                "Please select an image."
            );

        }


        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
        ];


        if (
            !allowedTypes.includes(
                String(
                    file.type ||
                    ""
                ).toLowerCase()
            )
        ) {

            throw new Error(
                "Only PNG, JPG, JPEG and WebP images are allowed."
            );

        }


        if (
            file.size >
            maxInputBytes
        ) {

            throw new Error(
                `Image is too large. Maximum upload size is ${Math.round(
                    maxInputBytes /
                    MB
                )} MB before optimization.`
            );

        }

    };


/*
|--------------------------------------------------------------------------
| AUTO COMPRESS INVOICE LOGO
|--------------------------------------------------------------------------
|
| Final stored logo target: <= 1 MB.
|
| If the selected image is already <= 1 MB it is returned unchanged.
| Larger images are automatically resized/compressed in the browser.
|
|--------------------------------------------------------------------------
*/

const optimizeInvoiceLogo =
    async (
        file
    ) => {

        validateImageFile(
            file,
            LOGO_INPUT_MAX_SIZE_BYTES
        );


        if (
            file.size <=
            LOGO_MAX_SIZE_BYTES
        ) {

            return {
                file,

                optimized:
                    false,

                originalSize:
                    file.size,

                finalSize:
                    file.size,
            };

        }


        let optimizedFile =
            await imageCompression(
                file,
                {
                    maxSizeMB:
                        0.9,

                    maxWidthOrHeight:
                        1400,

                    useWebWorker:
                        true,

                    initialQuality:
                        0.86,

                    alwaysKeepResolution:
                        false,
                }
            );


        /*
        |--------------------------------------------------------------------------
        | SECOND PASS
        |--------------------------------------------------------------------------
        */

        if (
            optimizedFile.size >
            LOGO_MAX_SIZE_BYTES
        ) {

            optimizedFile =
                await imageCompression(
                    optimizedFile,
                    {
                        maxSizeMB:
                            0.8,

                        maxWidthOrHeight:
                            1100,

                        useWebWorker:
                            true,

                        initialQuality:
                            0.76,

                        alwaysKeepResolution:
                            false,
                    }
                );

        }


        /*
        |--------------------------------------------------------------------------
        | FINAL SAFETY
        |--------------------------------------------------------------------------
        */

        if (
            optimizedFile.size >
            LOGO_MAX_SIZE_BYTES
        ) {

            throw new Error(
                "Unable to optimize this logo below 1 MB. Please choose another image."
            );

        }


        return {
            file:
                optimizedFile,

            optimized:
                true,

            originalSize:
                file.size,

            finalSize:
                optimizedFile.size,
        };

    };


/*
|--------------------------------------------------------------------------
| SIGNATURE IMAGE
|--------------------------------------------------------------------------
|
| Signature behavior is preserved.
| No automatic compression is introduced here because signatures are already
| intentionally limited to a small size.
|
|--------------------------------------------------------------------------
*/

const signatureFileToDataUrl =
    async (
        file
    ) => {

        validateImageFile(
            file,
            SIGNATURE_MAX_SIZE_BYTES
        );


        return fileToDataUrl(
            file
        );

    };


const InvoiceMiniPreview =
    ({
        type,
    }) => (

        <div
            className={[
                "invoice-template-mini-preview",
                `invoice-template-mini-preview--${type}`,
            ].join(
                " "
            )}
        >

            <div className="invoice-template-mini-preview__top">
                <div className="invoice-template-mini-preview__brand" />
                <div className="invoice-template-mini-preview__title" />
            </div>

            <div className="invoice-template-mini-preview__meta">
                <span />
                <span />
                <span />
            </div>

            <div className="invoice-template-mini-preview__table">
                <i />
                <i />
                <i />
                <i />
            </div>

            <div className="invoice-template-mini-preview__bottom">
                <div />
                <strong />
            </div>

        </div>

    );


const InvoiceTemplateSettings =
    () => {

        const dispatch =
            useDispatch();


        const tenant =
            useSelector(
                selectTenant
            );


        const isOwner =
            useSelector(
                selectIsTenantOwner
            );


        const savedTemplate =
            useSelector(
                selectTenantInvoiceTemplate
            );


        const templateUpdating =
            useSelector(
                selectTenantInvoiceTemplateUpdating
            );


        const templateError =
            useSelector(
                selectTenantInvoiceTemplateUpdateError
            );


        const templateSuccess =
            useSelector(
                selectTenantInvoiceTemplateUpdateSuccess
            );


        const savedBranding =
            useSelector(
                selectTenantInvoiceBranding
            );


        const brandingUpdating =
            useSelector(
                selectTenantInvoiceBrandingUpdating
            );


        const brandingError =
            useSelector(
                selectTenantInvoiceBrandingUpdateError
            );


        const brandingSuccess =
            useSelector(
                selectTenantInvoiceBrandingUpdateSuccess
            );


        const logoInputRef =
            useRef(
                null
            );


        const signatureInputRef =
            useRef(
                null
            );


        const [
            selectedTemplate,
            setSelectedTemplate,
        ] =
            useState(
                savedTemplate ||
                "MODERN_GST"
            );


        const [
            invoiceLogo,
            setInvoiceLogo,
        ] =
            useState(
                savedBranding
                    ?.logo ||
                ""
            );


        const [
            signatureMode,
            setSignatureMode,
        ] =
            useState(
                savedBranding
                    ?.signatureMode ||
                "NONE"
            );


        const [
            signatureImage,
            setSignatureImage,
        ] =
            useState(
                savedBranding
                    ?.signatureImage ||
                ""
            );


        const [
            signatureText,
            setSignatureText,
        ] =
            useState(
                savedBranding
                    ?.signatureText ||
                ""
            );


        const [
            signatureStyle,
            setSignatureStyle,
        ] =
            useState(
                savedBranding
                    ?.signatureStyle ||
                "STYLE_1"
            );


        const [
            localError,
            setLocalError,
        ] =
            useState(
                ""
            );


        const [
            logoProcessing,
            setLogoProcessing,
        ] =
            useState(
                false
            );


        const [
            logoOptimizationInfo,
            setLogoOptimizationInfo,
        ] =
            useState(
                null
            );


        useEffect(
            () => {

                setSelectedTemplate(
                    savedTemplate ||
                    "MODERN_GST"
                );

            },
            [
                savedTemplate,
            ]
        );


        useEffect(
            () => {

                setInvoiceLogo(
                    savedBranding
                        ?.logo ||
                    ""
                );

                setSignatureMode(
                    savedBranding
                        ?.signatureMode ||
                    "NONE"
                );

                setSignatureImage(
                    savedBranding
                        ?.signatureImage ||
                    ""
                );

                setSignatureText(
                    savedBranding
                        ?.signatureText ||
                    ""
                );

                setSignatureStyle(
                    savedBranding
                        ?.signatureStyle ||
                    "STYLE_1"
                );

            },
            [
                savedBranding,
            ]
        );


        const selectedOption =
            useMemo(
                () =>
                    TEMPLATE_OPTIONS.find(
                        (
                            item
                        ) =>
                            item.code ===
                            selectedTemplate
                    ) ||
                    TEMPLATE_OPTIONS[0],
                [
                    selectedTemplate,
                ]
            );


        const templateHasChanges =
            selectedTemplate !==
            savedTemplate;


        const brandingHasChanges =
            useMemo(
                () => {

                    return JSON.stringify({
                        logo:
                            invoiceLogo ||
                            "",

                        signatureMode,

                        signatureImage:
                            signatureImage ||
                            "",

                        signatureText:
                            signatureText ||
                            "",

                        signatureStyle,
                    }) !==
                    JSON.stringify({
                        logo:
                            savedBranding
                                ?.logo ||
                            "",

                        signatureMode:
                            savedBranding
                                ?.signatureMode ||
                            "NONE",

                        signatureImage:
                            savedBranding
                                ?.signatureImage ||
                            "",

                        signatureText:
                            savedBranding
                                ?.signatureText ||
                            "",

                        signatureStyle:
                            savedBranding
                                ?.signatureStyle ||
                            "STYLE_1",
                    });

                },
                [
                    invoiceLogo,
                    signatureMode,
                    signatureImage,
                    signatureText,
                    signatureStyle,
                    savedBranding,
                ]
            );


        const businessLogo =
            tenant
                ?.branding
                ?.logo ||
            "";


        const previewLogo =
            invoiceLogo ||
            businessLogo ||
            "";


        const handleSaveTemplate =
            async () => {

                if (
                    !isOwner ||
                    !templateHasChanges ||
                    templateUpdating
                ) {

                    return;
                }


                const result =
                    await dispatch(
                        updateTenantInvoiceTemplate(
                            selectedTemplate
                        )
                    );


                if (
                    updateTenantInvoiceTemplate
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await dispatch(
                        fetchTenantProfile()
                    );

                }

            };


        const handleLogoUpload =
            async (
                event
            ) => {

                setLocalError(
                    ""
                );


                setLogoOptimizationInfo(
                    null
                );


                const selectedFile =
                    event.target
                        .files?.[0];


                if (
                    !selectedFile
                ) {

                    event.target.value =
                        "";

                    return;

                }


                setLogoProcessing(
                    true
                );


                try {

                    const result =
                        await optimizeInvoiceLogo(
                            selectedFile
                        );


                    const image =
                        await fileToDataUrl(
                            result.file
                        );


                    setInvoiceLogo(
                        image
                    );


                    setLogoOptimizationInfo({
                        optimized:
                            result.optimized,

                        originalSize:
                            result.originalSize,

                        finalSize:
                            result.finalSize,
                    });

                } catch (
                    error
                ) {

                    setLocalError(
                        error?.message ||
                        "Unable to process invoice logo."
                    );

                } finally {

                    setLogoProcessing(
                        false
                    );


                    event.target.value =
                        "";

                }

            };


        const handleSignatureUpload =
            async (
                event
            ) => {

                setLocalError(
                    ""
                );


                try {

                    const image =
                        await signatureFileToDataUrl(
                            event.target
                                .files?.[0]
                        );


                    setSignatureImage(
                        image
                    );

                    setSignatureMode(
                        "UPLOAD"
                    );

                } catch (
                    error
                ) {

                    setLocalError(
                        error?.message ||
                        "Unable to load signature."
                    );

                } finally {

                    event.target.value =
                        "";

                }

            };


        const changeSignatureMode =
            (
                mode
            ) => {

                setLocalError(
                    ""
                );

                setSignatureMode(
                    mode
                );


                if (
                    mode ===
                    "NONE"
                ) {

                    setSignatureImage(
                        ""
                    );

                    setSignatureText(
                        ""
                    );

                } else if (
                    mode ===
                    "TEXT"
                ) {

                    setSignatureImage(
                        ""
                    );

                } else {

                    setSignatureText(
                        ""
                    );

                    setSignatureImage(
                        ""
                    );

                }

            };


        const handleSaveBranding =
            async () => {

                if (
                    !isOwner ||
                    !brandingHasChanges ||
                    brandingUpdating
                ) {

                    return;
                }


                setLocalError(
                    ""
                );


                if (
                    (
                        signatureMode ===
                        "UPLOAD" ||
                        signatureMode ===
                        "DRAW"
                    ) &&
                    !signatureImage
                ) {

                    setLocalError(
                        signatureMode ===
                        "DRAW"
                            ?
                            "Please draw the signature before saving."
                            :
                            "Please upload the signature before saving."
                    );

                    return;
                }


                if (
                    signatureMode ===
                    "TEXT" &&
                    !String(
                        signatureText ||
                        ""
                    ).trim()
                ) {

                    setLocalError(
                        "Please enter the authorized signatory name."
                    );

                    return;
                }


                const result =
                    await dispatch(
                        updateTenantInvoiceBranding({
                            logo:
                                invoiceLogo ||
                                "",

                            signatureMode,

                            signatureImage:
                                (
                                    signatureMode ===
                                    "UPLOAD" ||
                                    signatureMode ===
                                    "DRAW"
                                )
                                    ?
                                    signatureImage
                                    :
                                    "",

                            signatureText:
                                signatureMode ===
                                "TEXT"
                                    ?
                                    String(
                                        signatureText
                                    ).trim()
                                    :
                                    "",

                            signatureStyle,
                        })
                    );


                if (
                    updateTenantInvoiceBranding
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await dispatch(
                        fetchTenantProfile()
                    );

                }

            };


        return (

            <div className="invoice-template-settings">

                <div className="settings-section-header">

                    <div>

                        <h2>
                            Invoice Template
                        </h2>

                        <p>
                            Choose the default sale invoice design and configure
                            invoice logo and authorized signature for this business.
                            You can still temporarily change the template from an
                            individual invoice print page.
                        </p>

                    </div>

                </div>


                {
                    !isOwner &&
                    (
                        <div className="settings-warning">
                            Only the business owner can change invoice templates
                            and invoice branding.
                        </div>
                    )
                }


                {
                    templateSuccess &&
                    (
                        <div className="settings-success">
                            Default invoice template updated successfully.
                        </div>
                    )
                }


                {
                    brandingSuccess &&
                    (
                        <div className="settings-success">
                            Invoice logo and signature updated successfully.
                        </div>
                    )
                }


                {
                    (
                        localError ||
                        templateError ||
                        brandingError
                    ) &&
                    (
                        <div className="settings-error">
                            {
                                localError ||
                                templateError
                                    ?.message ||
                                brandingError
                                    ?.message ||
                                "Unable to update invoice settings."
                            }
                        </div>
                    )
                }


                <div className="invoice-template-current">

                    <div className="invoice-template-current__icon">
                        <FaFileInvoice />
                    </div>

                    <div>
                        <span>Current Default</span>

                        <strong>
                            {
                                TEMPLATE_OPTIONS.find(
                                    (
                                        item
                                    ) =>
                                        item.code ===
                                        savedTemplate
                                )
                                    ?.title ||
                                "Modern GST"
                            }
                        </strong>
                    </div>

                    <div className="invoice-template-current__badge">
                        <FaCheckCircle />
                        Active
                    </div>

                </div>


                <div className="invoice-template-grid">

                    {
                        TEMPLATE_OPTIONS.map(
                            (
                                template
                            ) => {

                                const selected =
                                    template.code ===
                                    selectedTemplate;


                                const currentDefault =
                                    template.code ===
                                    savedTemplate;


                                return (

                                    <button
                                        key={
                                            template.code
                                        }

                                        type="button"

                                        disabled={
                                            !isOwner ||
                                            templateUpdating
                                        }

                                        className={[
                                            "invoice-template-card",
                                            selected
                                                ?
                                                "selected"
                                                :
                                                "",
                                            currentDefault
                                                ?
                                                "default"
                                                :
                                                "",
                                        ]
                                            .filter(
                                                Boolean
                                            )
                                            .join(
                                                " "
                                            )}

                                        onClick={
                                            () =>
                                                setSelectedTemplate(
                                                    template.code
                                                )
                                        }
                                    >

                                        <div className="invoice-template-card__preview">

                                            <InvoiceMiniPreview
                                                type={
                                                    template.type
                                                }
                                            />

                                            {
                                                currentDefault &&
                                                (
                                                    <span className="invoice-template-card__default-badge">
                                                        <FaCheckCircle />
                                                        Default
                                                    </span>
                                                )
                                            }

                                        </div>


                                        <div className="invoice-template-card__body">

                                            <div>

                                                {
                                                    template.code ===
                                                    "THERMAL"
                                                        ?
                                                        <FaReceipt />
                                                        :
                                                        <FaFileInvoice />
                                                }

                                                <strong>
                                                    {template.title}
                                                </strong>

                                            </div>

                                            <p>
                                                {template.description}
                                            </p>

                                            <span className="invoice-template-card__select">
                                                <FaRegEye />
                                                {
                                                    selected
                                                        ?
                                                        "Selected"
                                                        :
                                                        "Select Template"
                                                }
                                            </span>

                                        </div>

                                    </button>

                                );

                            }
                        )
                    }

                </div>


                <div className="invoice-template-selection-summary">

                    <div>
                        <span>Selected Template</span>
                        <strong>{selectedOption.title}</strong>
                        <p>{selectedOption.description}</p>
                    </div>

                    <button
                        type="button"
                        className="settings-save-button"
                        disabled={
                            !isOwner ||
                            templateUpdating ||
                            !templateHasChanges
                        }
                        onClick={
                            handleSaveTemplate
                        }
                    >
                        {
                            templateUpdating
                                ?
                                "Saving..."
                                :
                                (
                                    templateHasChanges
                                        ?
                                        "Save as Default"
                                        :
                                        "Current Default"
                                )
                        }
                    </button>

                </div>


                <div className="invoice-branding-section">

                    <div className="invoice-branding-section__header">
                        <span>INVOICE BRANDING</span>

                        <h3>
                            Logo & Authorized Signature
                        </h3>

                        <p>
                            Used automatically by every invoice template.
                            Invoice-specific logo overrides the normal business logo.
                            If no logo exists, business initials are shown.
                        </p>
                    </div>


                    <div className="invoice-branding-card">

                        <div className="invoice-branding-card__heading">

                            <div className="invoice-branding-card__icon">
                                <FaImage />
                            </div>

                            <div>
                                <strong>Invoice Logo</strong>

                                <span>
                                    Upload an invoice-specific logo or keep using
                                    the existing business logo.
                                </span>
                            </div>

                        </div>


                        <div className="invoice-branding-logo-grid">

                            <div className="invoice-branding-logo-preview">

                                {
                                    previewLogo
                                        ?
                                        (
                                            <img
                                                src={
                                                    previewLogo
                                                }
                                                alt="Invoice logo preview"
                                            />
                                        )
                                        :
                                        (
                                            <div>
                                                Auto initials will be used
                                            </div>
                                        )
                                }

                            </div>


                            <div className="invoice-branding-actions">

                                <input
                                    ref={
                                        logoInputRef
                                    }
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    hidden
                                    onChange={
                                        handleLogoUpload
                                    }
                                />

                                <button
                                    type="button"
                                    className="invoice-branding-btn"
                                    disabled={
                                        !isOwner ||
                                        brandingUpdating ||
                                        logoProcessing
                                    }
                                    onClick={
                                        () =>
                                            logoInputRef
                                                .current
                                                ?.click()
                                    }
                                >
                                    <FaUpload />

                                    {
                                        logoProcessing
                                            ?
                                            "Optimizing Logo..."
                                            :
                                            "Upload Invoice Logo"
                                    }
                                </button>


                                {
                                    invoiceLogo &&
                                    businessLogo &&
                                    (
                                        <button
                                            type="button"
                                            className="invoice-branding-btn"
                                            disabled={
                                                !isOwner ||
                                                brandingUpdating
                                            }
                                            onClick={
                                                () =>
                                                    setInvoiceLogo(
                                                        ""
                                                    )
                                            }
                                        >
                                            Use Business Logo
                                        </button>
                                    )
                                }


                                {
                                    invoiceLogo &&
                                    (
                                        <button
                                            type="button"
                                            className="invoice-branding-btn invoice-branding-btn--danger"
                                            disabled={
                                                !isOwner ||
                                                brandingUpdating
                                            }
                                            onClick={
                                                () =>
                                                    setInvoiceLogo(
                                                        ""
                                                    )
                                            }
                                        >
                                            <FaTrash />
                                            Remove Invoice Logo
                                        </button>
                                    )
                                }

                                <small>
                                    PNG/JPG/WebP. Final logo size up to 1 MB.
                                    Images above 1 MB are optimized automatically.
                                    Transparent PNG is recommended.
                                </small>


                                {
                                    logoOptimizationInfo &&
                                    (
                                        <div className="invoice-logo-optimization-info">

                                            {
                                                logoOptimizationInfo.optimized
                                                    ?
                                                    (
                                                        <>
                                                            <strong>
                                                                ✓ Logo optimized automatically
                                                            </strong>

                                                            <span>
                                                                {
                                                                    (
                                                                        logoOptimizationInfo.originalSize /
                                                                        1024
                                                                    ).toFixed(
                                                                        0
                                                                    )
                                                                }
                                                                {" KB → "}
                                                                {
                                                                    (
                                                                        logoOptimizationInfo.finalSize /
                                                                        1024
                                                                    ).toFixed(
                                                                        0
                                                                    )
                                                                }
                                                                {" KB"}
                                                            </span>
                                                        </>
                                                    )
                                                    :
                                                    (
                                                        <>
                                                            <strong>
                                                                ✓ Logo ready
                                                            </strong>

                                                            <span>
                                                                {
                                                                    (
                                                                        logoOptimizationInfo.finalSize /
                                                                        1024
                                                                    ).toFixed(
                                                                        0
                                                                    )
                                                                }
                                                                {" KB"}
                                                            </span>
                                                        </>
                                                    )
                                            }

                                        </div>
                                    )
                                }

                            </div>

                        </div>

                    </div>


                    <div className="invoice-branding-card">

                        <div className="invoice-branding-card__heading">

                            <div className="invoice-branding-card__icon">
                                <FaSignature />
                            </div>

                            <div>
                                <strong>Authorized Signature</strong>

                                <span>
                                    Upload, draw with mouse/trackpad/stylus/touch,
                                    or create a typed signature.
                                </span>
                            </div>

                        </div>


                        <div className="invoice-signature-modes">

                            {
                                SIGNATURE_MODES.map(
                                    (
                                        [
                                            code,
                                            label,
                                            Icon,
                                        ]
                                    ) => (

                                        <button
                                            key={
                                                code
                                            }
                                            type="button"
                                            disabled={
                                                !isOwner ||
                                                brandingUpdating
                                            }
                                            className={[
                                                "invoice-signature-mode",
                                                signatureMode ===
                                                code
                                                    ?
                                                    "active"
                                                    :
                                                    "",
                                            ]
                                                .filter(
                                                    Boolean
                                                )
                                                .join(
                                                    " "
                                                )}
                                            onClick={
                                                () =>
                                                    changeSignatureMode(
                                                        code
                                                    )
                                            }
                                        >
                                            <Icon />
                                            {label}
                                        </button>

                                    )
                                )
                            }

                        </div>


                        {
                            signatureMode ===
                            "UPLOAD" &&
                            (
                                <div className="invoice-signature-editor">

                                    <div className="invoice-signature-preview">
                                        {
                                            signatureImage
                                                ?
                                                (
                                                    <img
                                                        src={
                                                            signatureImage
                                                        }
                                                        alt="Authorized signature preview"
                                                    />
                                                )
                                                :
                                                (
                                                    <span>
                                                        No signature uploaded
                                                    </span>
                                                )
                                        }
                                    </div>

                                    <div className="invoice-branding-actions">

                                        <input
                                            ref={
                                                signatureInputRef
                                            }
                                            type="file"
                                            accept="image/png,image/jpeg,image/webp"
                                            hidden
                                            onChange={
                                                handleSignatureUpload
                                            }
                                        />

                                        <button
                                            type="button"
                                            className="invoice-branding-btn"
                                            disabled={
                                                !isOwner ||
                                                brandingUpdating
                                            }
                                            onClick={
                                                () =>
                                                    signatureInputRef
                                                        .current
                                                        ?.click()
                                            }
                                        >
                                            <FaUpload />
                                            Upload Signature
                                        </button>

                                        {
                                            signatureImage &&
                                            (
                                                <button
                                                    type="button"
                                                    className="invoice-branding-btn invoice-branding-btn--danger"
                                                    disabled={
                                                        !isOwner ||
                                                        brandingUpdating
                                                    }
                                                    onClick={
                                                        () =>
                                                            setSignatureImage(
                                                                ""
                                                            )
                                                    }
                                                >
                                                    <FaTrash />
                                                    Remove
                                                </button>
                                            )
                                        }

                                        <small>
                                            Maximum 350 KB. Transparent PNG works best.
                                        </small>

                                    </div>

                                </div>
                            )
                        }


                        {
                            signatureMode ===
                            "DRAW" &&
                            (
                                <div className="invoice-signature-draw">
                                    <SignaturePad
                                        value={
                                            signatureImage
                                        }
                                        disabled={
                                            !isOwner ||
                                            brandingUpdating
                                        }
                                        onChange={
                                            setSignatureImage
                                        }
                                    />
                                </div>
                            )
                        }


                        {
                            signatureMode ===
                            "TEXT" &&
                            (
                                <div className="invoice-signature-text-grid">

                                    <div className="settings-field">

                                        <label>
                                            Authorized Signatory Name
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                signatureText
                                            }
                                            maxLength={
                                                100
                                            }
                                            disabled={
                                                !isOwner ||
                                                brandingUpdating
                                            }
                                            placeholder="Example: Vijendra Choudhary"
                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    setSignatureText(
                                                        event.target.value
                                                    )
                                            }
                                        />

                                    </div>


                                    <div className="settings-field">

                                        <label>
                                            Signature Style
                                        </label>

                                        <select
                                            value={
                                                signatureStyle
                                            }
                                            disabled={
                                                !isOwner ||
                                                brandingUpdating
                                            }
                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    setSignatureStyle(
                                                        event.target.value
                                                    )
                                            }
                                        >
                                            <option value="STYLE_1">
                                                Signature Style 1
                                            </option>

                                            <option value="STYLE_2">
                                                Signature Style 2
                                            </option>

                                            <option value="STYLE_3">
                                                Signature Style 3
                                            </option>
                                        </select>

                                    </div>


                                    <div
                                        className={[
                                            "invoice-signature-text-preview",
                                            `invoice-signature-text-preview--${signatureStyle.toLowerCase()}`,
                                        ].join(
                                            " "
                                        )}
                                    >
                                        {
                                            signatureText ||
                                            "Your Signature"
                                        }
                                    </div>

                                </div>
                            )
                        }


                        {
                            signatureMode ===
                            "NONE" &&
                            (
                                <div className="invoice-signature-disabled">
                                    Authorized signature will not be printed.
                                </div>
                            )
                        }

                    </div>


                    <div className="invoice-template-selection-summary">

                        <div>
                            <span>Invoice Branding</span>

                            <strong>
                                {
                                    brandingHasChanges
                                        ?
                                        "Unsaved Changes"
                                        :
                                        "Saved"
                                }
                            </strong>

                            <p>
                                Logo and signature are shared automatically by
                                Modern GST, Compact, Retail, Service, Thermal,
                                Professional and Minimal templates.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="settings-save-button"
                            disabled={
                                !isOwner ||
                                brandingUpdating ||
                                !brandingHasChanges
                            }
                            onClick={
                                handleSaveBranding
                            }
                        >
                            {
                                brandingUpdating
                                    ?
                                    "Saving..."
                                    :
                                    (
                                        brandingHasChanges
                                            ?
                                            "Save Invoice Branding"
                                            :
                                            "Branding Saved"
                                    )
                            }
                        </button>

                    </div>

                </div>

            </div>

        );

    };


export default InvoiceTemplateSettings;
